import React, { useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';

import { PackagingPOStatusCell } from 'components/packaging/components/packagingPOV2/PackagingPOStatusCell';
import {
    formatPurchaseOrderDate,
    formatPurchaseOrderDateTime,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import {
    FALLBACK_TEXT,
    formatCurrency,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { PaginationControls, ResultsCounter } from 'shared';

const SERVICE_FEE = 'SERVICE_FEE';
const SHIPPING_FEE = 'SHIPPING_FEE';

function feeTotal(additionalFees, chargeType) {
    const match = (additionalFees ?? []).find((fee) => fee.chargeType === chargeType);
    if (match?.totalRate == null) {
        return null;
    }
    const numeric = Number(match.totalRate);
    return Number.isFinite(numeric) ? numeric : null;
}

function additionalFeesTotal(additionalFees) {
    return (additionalFees ?? []).reduce((sum, fee) => {
        if (fee.chargeType === SERVICE_FEE || fee.chargeType === SHIPPING_FEE) {
            return sum;
        }
        const numeric = Number(fee.totalRate ?? 0);
        return Number.isFinite(numeric) ? sum + numeric : sum;
    }, 0);
}

function formatPoNumbers(poMaps) {
    const labels = (poMaps ?? [])
        .map((map) => {
            if (map.purchaseOrderId != null) {
                return String(map.purchaseOrderId);
            }
            if (map.packagingPurchaseOrderId != null) {
                return `PPO-${map.packagingPurchaseOrderId}`;
            }
            return null;
        })
        .filter(Boolean);
    return labels.length ? labels.join(', ') : FALLBACK_TEXT;
}

function formatFee(value) {
    return value == null ? FALLBACK_TEXT : formatCurrency(value);
}

const renderStatusCell = ({ getValue }) => <PackagingPOStatusCell status={getValue()} fallbackText={FALLBACK_TEXT} />;

/**
 * @param {Object} props
 * @param {Array<Object>} [props.data=[]]
 * @param {Map<number, string>} [props.facilityNameById]
 * @param {Map<number, string>} [props.crossDockNameById]
 * @param {number} [props.totalCount=0]
 * @param {boolean} [props.isLoading=false]
 * @param {boolean} [props.isFetching=false]
 * @param {boolean} [props.hasNextPage=false]
 * @param {boolean} [props.hasPrevious=false]
 * @param {() => void} [props.onPrevious]
 * @param {() => void} [props.onNext]
 * @param {number} [props.currentPage]
 * @param {number} [props.totalPages]
 * @param {number} [props.pageSize]
 * @param {(size: number) => void} [props.onPageSizeChange]
 * @param {number[]} [props.pageSizeOptions]
 */
export function CrossDockPurchaseOrdersTable({
    crossDockNameById,
    currentPage,
    data = [],
    facilityNameById,
    hasNextPage = false,
    hasPrevious = false,
    isFetching = false,
    isLoading = false,
    onNext,
    onPageSizeChange,
    onPrevious,
    pageSize,
    pageSizeOptions,
    totalCount = 0,
    totalPages,
}) {
    const tableData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

    const columns = useMemo(
        () => [
            {
                header: 'Facility',
                id: 'facility',
                accessorFn: (row) => facilityNameById?.get(Number(row.facilityId)) ?? row.facilityId,
                cell: ({ getValue }) => getValue() ?? FALLBACK_TEXT,
            },
            {
                header: 'PO Number(s)',
                id: 'poNumbers',
                accessorFn: (row) => formatPoNumbers(row.poMaps),
            },
            {
                header: 'Created On',
                accessorKey: 'createdAt',
                cell: ({ getValue }) => formatPurchaseOrderDateTime(getValue()),
            },
            {
                header: 'Status',
                accessorKey: 'status',
                cell: renderStatusCell,
            },
            {
                header: 'Destination Cross Dock',
                id: 'crossDock',
                accessorFn: (row) => crossDockNameById?.get(Number(row.crossDockId)) ?? row.crossDockId,
                cell: ({ getValue }) => getValue() ?? FALLBACK_TEXT,
            },
            {
                header: 'Scheduled Delivery',
                id: 'scheduledDelivery',
                accessorFn: (row) => {
                    const date = formatPurchaseOrderDate(row.scheduledDeliveryDate, '');
                    const time = row.scheduledDeliveryTime;
                    if (date && time) {
                        return `${date} ${time}`;
                    }
                    return date || time || FALLBACK_TEXT;
                },
            },
            {
                header: 'Service Fee',
                id: 'serviceFee',
                accessorFn: (row) => feeTotal(row.additionalFees, SERVICE_FEE),
                cell: ({ getValue }) => formatFee(getValue()),
            },
            {
                header: 'Additional Fees',
                id: 'additionalFees',
                accessorFn: (row) => additionalFeesTotal(row.additionalFees),
                cell: ({ getValue }) => {
                    const total = getValue();
                    return total ? formatFee(total) : FALLBACK_TEXT;
                },
            },
            {
                header: 'Shipping Fee',
                id: 'shippingFee',
                accessorFn: (row) => feeTotal(row.additionalFees, SHIPPING_FEE),
                cell: ({ getValue }) => formatFee(getValue()),
            },
            {
                header: 'Actions',
                id: 'actions',
                cell: () => FALLBACK_TEXT,
            },
        ],
        [crossDockNameById, facilityNameById],
    );

    const table = useReactTable({
        columns,
        data: tableData,
        getCoreRowModel: getCoreRowModel(),
    });

    const showPagination = totalCount > 0 && (hasPrevious || hasNextPage);

    return (
        <div className="flex flex-col gap-4">
            <ResultsCounter
                loadedCount={tableData.length}
                totalCount={totalCount}
                isRefreshing={isFetching && !isLoading}
            />
            <div className="relative rounded-md border">
                {(isLoading || isFetching) && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                )}
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id} className="whitespace-normal break-words">
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id}>
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id} className="whitespace-normal break-words text-left">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    No cross dock purchase orders found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            {showPagination ? (
                <PaginationControls
                    hasPrevious={hasPrevious}
                    hasNext={hasNextPage}
                    onPrevious={onPrevious}
                    onNext={onNext}
                    isLoading={isFetching}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    onPageSizeChange={onPageSizeChange}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : null}
        </div>
    );
}
